/**
 * Normalises rows from GET /facilities/:id/alerts into the realtime slice's Alert shape.
 *
 * The REST list returns `facility_id` (not `site_id`) and ISO-8601 strings for
 * `timestamp`, `acknowledged_at`, and `resolved_at`; the slice and the stream
 * mapper (services/websocket.ts) use `site_id` and epoch milliseconds.
 */
import type { Alert } from '@/types';

export type RestAlertRow = Omit<Alert, 'timestamp' | 'acknowledged_at' | 'resolved_at'> & {
  facility_id?: string;
  timestamp: number | string;
  acknowledged_at?: number | string;
  resolved_at?: number | string;
};

function toEpochMs(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

/**
 * Maps one REST alert row to the slice shape; `facilityId` fills `site_id` when the row lacks it.
 * Returns null for a row without a usable id so it can never enter the slice as "undefined".
 */
export function normalizeSeededAlert(raw: RestAlertRow, facilityId: string): Alert | null {
  const id = raw?.id == null ? '' : String(raw.id).trim();
  if (!id) return null;
  return {
    ...raw,
    id,
    site_id: raw.site_id || raw.facility_id || facilityId,
    timestamp: toEpochMs(raw.timestamp) ?? Date.now(),
    acknowledged: Boolean(raw.acknowledged),
    acknowledged_at: toEpochMs(raw.acknowledged_at),
    resolved: Boolean(raw.resolved),
    resolved_at: toEpochMs(raw.resolved_at),
  };
}
